<template>
  <el-dialog
    v-model="dialogVisible"
    title="Excel orqali yuklash"
    width="600px"
    append-to-body
    @closed="reset"
  >
    <div class="upload-body">
      <el-row :gutter="12">
        <el-col :span="12">
          <div class="field-label">Yil</div>
          <el-select v-model="year" style="width: 100%" placeholder="Yil">
            <el-option v-for="y in years" :key="y" :value="y" :label="y" />
          </el-select>
        </el-col>
        <el-col :span="12">
          <div class="field-label">Chorak</div>
          <el-radio-group v-model="quarter">
            <el-radio-button v-for="q in 4" :key="q" :label="q">{{ q }}-chorak</el-radio-button>
          </el-radio-group>
        </el-col>
      </el-row>

      <div class="upload-format">
        <div class="upload-format__title">Fayl formati (majburiy):</div>
        <ul>
          <li>Bitta <b>.xlsx</b>/<b>.xls</b> fayl, aynan ikkita varaq: <b>Regions</b> va <b>Districts</b>.</li>
          <li>Har varaqning 1-qatori (sarlavha): <b>soato, income, outcome, transfer</b>.</li>
          <li>Hudud SOATO kodi bo'yicha aniqlanadi. Faylga kiritilmagan hududlar o'zgarmaydi.</li>
        </ul>
        <el-link type="primary" :underline="false" @click="downloadSoatoReference">
          Viloyatlar SOATO ro'yxatini yuklab olish (ma'lumotnoma)
        </el-link>
        <div class="upload-format__note">
          Eslatma: tumanlar uchun SOATO kodlari API'da mavjud emas — ularni o'z manbangizdan oling.
        </div>
      </div>

      <el-upload
        ref="uploadRef"
        class="upload-drop"
        :action="action"
        :headers="headers"
        name="file"
        accept=".xlsx,.xls"
        :multiple="false"
        :limit="1"
        :auto-upload="false"
        :on-exceed="onExceed"
        :on-success="onSuccess"
        :on-error="onError"
        :on-change="onFileChange"
        :on-remove="onFileRemove"
        drag
      >
        <el-icon class="el-icon--upload"><upload-filled /></el-icon>
        <div class="el-upload__text">Faylni tashlang yoki <em>bu yerga bosing</em></div>
        <template #tip>
          <div class="el-upload__tip">Faqat .xlsx yoki .xls fayllar</div>
        </template>
      </el-upload>
    </div>

    <template #footer>
      <el-button @click="dialogVisible = false">Bekor qilish</el-button>
      <el-button
        type="primary"
        :loading="uploading"
        :disabled="!canSubmit"
        @click="submit"
      >
        Yuklash
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import { ElMessage } from "element-plus";
import { UploadFilled } from "@element-plus/icons-vue";
import api, { uploadUrl, uploadHeaders } from "./financeReportsApi";

export default {
  name: "FinanceReportUploadModal",
  components: { UploadFilled },
  props: {
    modelValue: { type: Boolean, default: false },
    defaultYear: { type: Number, default: null },
    defaultQuarter: { type: Number, default: null },
  },
  emits: ["update:modelValue", "uploaded"],
  data() {
    return {
      year: this.defaultYear || new Date().getFullYear(),
      quarter: this.defaultQuarter || 1,
      hasFile: false,
      uploading: false,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    years() {
      const current = new Date().getFullYear();
      const list = [];
      for (let y = current + 1; y >= 2020; y--) list.push(y);
      return list;
    },
    action() {
      return uploadUrl(this.year, this.quarter);
    },
    headers() {
      return uploadHeaders();
    },
    canSubmit() {
      return this.hasFile && !!this.year && !!this.quarter;
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.year = this.defaultYear || new Date().getFullYear();
        this.quarter = this.defaultQuarter || 1;
      }
    },
  },
  methods: {
    reset() {
      this.hasFile = false;
      this.uploading = false;
      this.$refs.uploadRef?.clearFiles();
    },
    onFileChange(file) {
      this.hasFile = !!file;
    },
    onFileRemove() {
      this.hasFile = false;
    },
    onExceed() {
      ElMessage.warning("Faqat bitta fayl yuklash mumkin!");
    },
    submit() {
      if (!this.canSubmit) return;
      this.uploading = true;
      this.$refs.uploadRef.submit();
    },
    onSuccess() {
      // Upload returns an empty {} body on success (spec §2.11) — HTTP 200 is
      // the signal; nothing to parse.
      this.uploading = false;
      ElMessage.success("Fayl muvaffaqiyatli yuklandi");
      this.$emit("uploaded");
      this.dialogVisible = false;
    },
    onError(error) {
      this.uploading = false;
      // The backend pinpoints the offending sheet/row in `message`; surface it
      // verbatim (spec §2.12 / §5.1). el-upload delivers the body as err.message.
      let message = "Yuklashda xatolik yuz berdi";
      try {
        message = JSON.parse(error?.message)?.message || message;
      } catch (e) {
        // keep default
      }
      ElMessage.error(message);
      // Let the admin fix and retry without reselecting: keep the file staged.
      this.$refs.uploadRef?.clearFiles();
      this.hasFile = false;
    },
    downloadSoatoReference() {
      api
        .listRegionsWithSoato()
        .then(({ data }) => {
          const rows = data.regions || [];
          const header = "soato,viloyat";
          const body = rows
            .map((r) => `${r.soato},"${(r.title || "").replace(/"/g, '""')}"`)
            .join("\n");
          const csv = "﻿" + header + "\n" + body + "\n";
          const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "viloyatlar-soato.csv");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        })
        .catch(() => ElMessage.error("SOATO ro'yxatini yuklashda xatolik"));
    },
  },
};
</script>

<style scoped>
.field-label {
  margin-bottom: 6px;
  color: #606266;
  font-size: 13px;
}
.upload-format {
  margin: 16px 0;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.5;
  color: #606266;
}
.upload-format__title {
  font-weight: 600;
  margin-bottom: 4px;
}
.upload-format ul {
  margin: 0 0 8px;
  padding-left: 18px;
}
.upload-format__note {
  margin-top: 6px;
  color: #e6a23c;
}
.upload-drop {
  margin-top: 8px;
}
</style>
